
/**
 * Create an action for when dominant speaker changes.
 *
 * {
 *     type: DOMINANT_SPEAKER_CHANGED,
 *     participant: {
 *         id: string
 *     }.
 * }.
 */
export const DOMINANT_SPEAKER_CHANGED = 'DOMINANT_SPEAKER_CHANGED';

/**
 * Create an action for granting moderator to a participant.
 *
 * {
 *     type: GRANT_MODERATOR,
 *     id: string
 * }.
 */
export const GRANT_MODERATOR = 'GRANT_MODERATOR';

/**
 * Create an action for setting the webinar role (panelist) of a participant.
 *
 * {
 *     type: SET_PANELIST,
 *     id: string,
 *     panelist: boolean
 * }.
 */
export const SET_PANELIST = 'SET_PANELIST';

/**
 * Create an action for setting the webinar presenter role of a participant.
 *
 * {
 *     type: SET_PRESENTER,
 *     id: string,
 *     presenter: boolean
 * }.
 */
export const SET_PRESENTER = 'SET_PRESENTER';

/**
 * Create an action for hiding an occupant from the audience (or un-hiding).
 * Server-verified moderator action; the target may be the sender themselves.
 *
 * {
 *     type: SET_WEBINAR_HIDDEN,
 *     id: string,
 *     hidden: boolean
 * }.
 */
export const SET_WEBINAR_HIDDEN = 'SET_WEBINAR_HIDDEN';

/**
 * Create an action for removing a participant from the conference.
 *
 * {
 *     type: KICK_PARTICIPANT,
 *     id: string
 * }.
 */
export const KICK_PARTICIPANT = 'KICK_PARTICIPANT';

/**
 * Create an action for muting a remote participant.
 *
 * {
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string
 * }.
 */
export const MUTE_REMOTE_PARTICIPANT = 'MUTE_REMOTE_PARTICIPANT';

/**
 * Create an action for when the local participant's display name is updated.
 *
 * {
 *     type: PARTICIPANT_DISPLAY_NAME_CHANGED,
 *     id: string,
 *     name: string
 * }.
 */
export const PARTICIPANT_DISPLAY_NAME_CHANGED
  = 'PARTICIPANT_DISPLAY_NAME_CHANGED';

/**
 * Action to signal that ID of participant has changed. This happens when
 * local participant joins a new conference or quits one.
 *
 * {
 *     type: PARTICIPANT_ID_CHANGED,
 *     conference: MHConference
 *     newValue: string,
 *     oldValue: string
 * }.
 */
export const PARTICIPANT_ID_CHANGED = 'PARTICIPANT_ID_CHANGED';

/**
 * Action to signal that participant role has changed. E.
 *
 * {
 *     type: PARTICIPANT_ROLE_CHANGED,
 *     participant: {
 *         id: string
 *     }.
 *     Role: string
 * }.
 */
export const PARTICIPANT_ROLE_CHANGED = 'PARTICIPANT_ROLE_CHANGED';

/**
 * Action to signal that a participant has joined.
 *
 * {
 *     type: PARTICIPANT_JOINED,
 *     participant: Participant
 * }.
 */
export const PARTICIPANT_JOINED = 'PARTICIPANT_JOINED';

/**
 * Action to signal that a participant has been removed from a conference by
 * another participant.
 *
 * {
 *     type: PARTICIPANT_KICKED,
 *     kicked: Object,
 *     kicker: Object
 * }.
 */
export const PARTICIPANT_KICKED = 'PARTICIPANT_KICKED';

/**
 * Action to handle case when participant lefts.
 *
 * {
 *     type: PARTICIPANT_LEFT,
 *     participant: {
 *         id: string
 *     }.
 * }.
 */
export const PARTICIPANT_LEFT = 'PARTICIPANT_LEFT';

/**
 * Action to handle case when info about participant changes.
 *
 * {
 *     type: PARTICIPANT_UPDATED,
 *     participant: Participant
 * }.
 */
export const PARTICIPANT_UPDATED = 'PARTICIPANT_UPDATED';

/**
 * The type of the Redux action which pins a conference participant.
 *
 * {
 *     type: PIN_PARTICIPANT,
 *     participant: {
 *         id: string,
 *        isPinned: boolean
 *     }.
 * }.
 */
export const PIN_PARTICIPANT = 'PIN_PARTICIPANT';

/**
 * Action to signal that a hidden participant has joined.
 *
 * {
 *     type: HIDDEN_PARTICIPANT_JOINED,
 *     participant: Participant
 * }.
 */
export const HIDDEN_PARTICIPANT_JOINED = 'HIDDEN_PARTICIPANT_JOINED';

/**
 * Action to handle case when hidden participant leaves.
 *
 * {
 *     type: PARTICIPANT_LEFT,
 *     participant: {
 *         id: string
 *     }.
 * }.
 */
export const HIDDEN_PARTICIPANT_LEFT = 'HIDDEN_PARTICIPANT_LEFT';

/**
 * The type of Redux action which notifies the app that the loadable avatar URL has changed.
 *
 * {
 *     type: SET_LOADABLE_AVATAR_URL,
 *     participant: {
 *         id: string,
           loadableAvatarUrl: string
 *     }.
 * }.
 */
export const SET_LOADABLE_AVATAR_URL = 'SET_LOADABLE_AVATAR_URL';

/**
 * Raises hand for the local participant.
 * {
 *     type: LOCAL_PARTICIPANT_RAISE_HAND
 * }.
 */
export const LOCAL_PARTICIPANT_RAISE_HAND = 'LOCAL_PARTICIPANT_RAISE_HAND';

/**
 * Clear the raise hand queue.
 * {
 *     type: RAISE_HAND_CLEAR
 * }.
 */
export const RAISE_HAND_CLEAR = 'RAISE_HAND_CLEAR';

/**
 * Updates participant in raise hand queue.
 * {
 *     type: RAISE_HAND_UPDATED,
 *     participant: {
 *         id: string,
 *         raiseHand: boolean
 *     }.
 * }.
 */
export const RAISE_HAND_UPDATED = 'RAISE_HAND_UPDATED';

/**
 * The type of Redux action which overwrites the name of a participant.
 * {
 *     type: OVERWRITE_PARTICIPANT_NAME,
 *     id: string,
 *     name: string
 * }
 */
export const OVERWRITE_PARTICIPANT_NAME = 'OVERWRITE_PARTICIPANT_NAME';

/**
 * The type of Redux action which overwrites the names of multiple participants.
 * {
 *     type: OVERWRITE_PARTICIPANTS_NAMES,
 *     participantsList: Array<Object>,
 * }
 */
export const OVERWRITE_PARTICIPANTS_NAMES = 'OVERWRITE_PARTICIPANTS_NAMES';

/**
 * The type of Redux action which notifies that the screenshare participant's display name has changed.
 *
 * {
 *     type: SCREENSHARE_PARTICIPANT_NAME_CHANGED,
 *     id: string,
 *     name: string
 * }
 */
export const SCREENSHARE_PARTICIPANT_NAME_CHANGED
  = 'SCREENSHARE_PARTICIPANT_NAME_CHANGED';

/**
 * Create an action for showing recording consent modal to a participant.
 *
 * {
 *     type: SHOW_RECORDING_CONSENT_MODAL,
 *     participant: { id: string }
 * }.
 */
export const SHOW_RECORDING_CONSENT_MODAL = 'SHOW_RECORDING_CONSENT_MODAL';

/**
 * Create an action for hiding recording consent modal from a participant.
 *
 * {
 *     type: HIDE_RECORDING_CONSENT_MODAL,
 *     participant: { id: string }
 * }.
 */
export const HIDE_RECORDING_CONSENT_MODAL = 'HIDE_RECORDING_CONSENT_MODAL';

/**
 * Create an action to determine who started the recording/
 *
 * {
 *     type: RECORDING_STARTED_BY_USER,
 *     participantId: string
 * }.
 */
export const RECORDING_STARTED_BY_USER = 'RECORDING_STARTED_BY_USER';

/**
 * Resets the isRecordingStartedByUser flag on the local participant when
 * recording stops, so subsequent recordings trigger the consent flow fresh.
 *
 * {
 *     type: RESET_RECORDING_STARTED_BY_USER,
 *     participant: { id: string }
 * }.
 */
export const RESET_RECORDING_STARTED_BY_USER = 'RESET_RECORDING_STARTED_BY_USER';

/**
 * The type of Redux action which notifies that the participant's color has changed.
 *
 * {
 *     type: SET_PARTICIPANT_COLOR,
 *     id: string,
 *     color: string
 * }
 */
export const SET_PARTICIPANT_COLOR = 'SET_PARTICIPANT_COLOR';

/**
 *  The type of Redux action which clears the raised hand status for a specific participant.
 *
 * {
 *     type: CLEAR_RAISED_HAND_BY_PARTICIPANT,
 *     id: string
 * }
 */
export const CLEAR_RAISED_HAND_BY_PARTICIPANT
  = 'CLEAR_RAISED_HAND_BY_PARTICIPANT';


/**
 * The type of Redux action which sets the moderator pin for a participant.
 *
 * {
 *     type: SET_MODERATOR_PIN,
 *     id: string,
 * }
 */
export const SET_MODERATOR_PIN = 'SET_MODERATOR_PIN';


/**
 * The type of Redux action which notifies that the participant's volume has changed.
 *
 * {
 *     type: PARTICIPANT_VOLUME_CHANGED,
 *     id: string,
 *     volume: number
 * }
 */
export const PARTICIPANT_VOLUME_CHANGED = 'PARTICIPANT_VOLUME_CHANGED';

/**
 * The type of Redux action which stores the participants designation.
 *
 * {
 *     type: PARTICIPANT_DESIGNATION,
 *     designation: string
 * }
 */
export const PARTICIPANT_DESIGNATION = 'PARTICIPANT_DESIGNATION';

/**
 * The type of Redux action which stores the participants organization.
 *
 * {
 *     type: PARTICIPANT_ORGANIZATION,
 *     organization: string
 * }
 */
export const PARTICIPANT_ORGANIZATION = 'PARTICIPANT_ORGANIZATION';
export const PARTICIPANT_COMPANY = 'PARTICIPANT_COMPANY';


/**
 * Updates participants local recording status.
 * {
 *     type: SET_LOCAL_PARTICIPANT_RECORDING_STATUS,
 *     recording: boolean,
 *     onlySelf: boolean
 * }
 */
export const SET_LOCAL_PARTICIPANT_RECORDING_STATUS = 'SET_LOCAL_PARTICIPANT_RECORDING_STATUS';

